import { availablePointsOfInterest } from '@/data/availablePOI'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormField, Select, type SelectOption } from '../../shared/ui'

export interface POIInfo extends Record<string, unknown> {
	id: string
	model: string
	name: string
	location?: string
	capabilities?: string[]
}

export interface POISelectorProperties {
	value: string
	onChange: (poiId: string) => void
	error?: string
	label?: string
	hint?: string
	className?: string
	disabled?: boolean
	autoFocus?: boolean
	filterByLocation?: string
	customPOIs?: POIInfo[]
}

export function POISelector({
	value,
	onChange,
	error,
	label,
	hint,
	className,
	disabled = false,
	autoFocus = false,
	filterByLocation,
	customPOIs,
}: POISelectorProperties) {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [searchTerm, setSearchTerm] = useState('')

	// Use custom POIs if provided, otherwise use default available POIs
	const poiData: POIInfo[] = useMemo(() => {
		const baseData = customPOIs || (availablePointsOfInterest as POIInfo[])

		// Filter by location if specified
		if (filterByLocation) {
			return baseData.filter((poi) => poi.location?.toLowerCase().includes(filterByLocation.toLowerCase()))
		}

		return baseData
	}, [customPOIs, filterByLocation])

	// Filter POIs based on search term
	const filteredPOIs = useMemo(() => {
		if (!searchTerm) return poiData

		const term = searchTerm.toLowerCase()
		return poiData.filter(
			(poi) =>
				poi.model.toLowerCase().includes(term) ||
				poi.location?.toLowerCase().includes(term) ||
				poi.name.toLowerCase().includes(term)
		)
	}, [poiData, searchTerm])

	// Convert POI data to select options
	const selectOptions: SelectOption[] = useMemo(() => {
		return filteredPOIs.map((poi) => ({
			value: poi.id,
			label: `${poi.model} - ${poi.location || poi.name}`,
			metadata: poi,
		}))
	}, [filteredPOIs])

	const handleChange = useCallback(
		(poiId: string) => {
			onChange(poiId)
		},
		[onChange]
	)

	const renderPOIOption = useCallback((option: SelectOption) => {
		const poi = option.metadata as POIInfo

		return (
			<div className="py-1">
				<div className="font-medium text-white">{poi.model}</div>
				{poi.location ? <div className="text-sm text-white/60">{poi.location}</div> : undefined}
			</div>
		)
	}, [])

	// Auto-focus effect
	useEffect(() => {
		if (autoFocus && !disabled) {
			// Focus will be handled by the Select component
		}
	}, [autoFocus, disabled])

	const hintText = hint

	return (
		<FormField label={label} error={error} hint={hintText} className={className}>
			<div className="space-y-3">
				<Select
					options={selectOptions}
					value={value}
					placeholder="Select a payment terminal"
					disabled={disabled}
					error={!!error}
					onChange={handleChange}
					renderOption={renderPOIOption}
					aria-label={label}
				/>

				{/* No results message */}
				{filteredPOIs.length === 0 ? (
					<div className="py-4 text-center text-white/60">
						No payment terminals found
						{filterByLocation ? ` in ${filterByLocation}` : undefined}
					</div>
				) : undefined}
			</div>
		</FormField>
	)
}
